import { z } from 'zod';
import {
  CATALOG,
  CATALOG_KEYS,
  buildWriteSchema,
  isAllowedCatalogValue,
  isBlockedValue,
  valueNote,
  type CatalogKey,
  type CatalogValue,
} from '../catalog/index.js';
import { RULE_DESCRIPTION_PREFIX } from '../catalog/constants.js';

/** Wartości ustawień wybrane w presecie. Wszystkie opcjonalne. */
export type PresetItems = { [K in CatalogKey]?: CatalogValue<K> };

type PresetItemsShape = {
  [K in CatalogKey]: z.ZodOptional<z.ZodType<CatalogValue<K>>>;
};

/**
 * Schemat WYPROWADZONY z katalogu. Dodanie ustawienia do katalogu automatycznie
 * rozszerza walidację — nie ma drugiego miejsca do zaktualizowania.
 */
const presetItemsShape = Object.fromEntries(
  CATALOG_KEYS.map((key) => [key, buildWriteSchema(CATALOG[key].value).optional()]),
) as PresetItemsShape;

export const presetItemsSchema = z.object(presetItemsShape);

// Dowód w czasie kompilacji, że typ wyprowadzony ze schematu i ręcznie
// zapisany `PresetItems` nie rozjechały się. Rozjazd = błąd kompilacji.
type InferredItems = z.infer<typeof presetItemsSchema>;
function _proveForward(v: InferredItems): PresetItems {
  return v;
}
function _proveBackward(v: PresetItems): InferredItems {
  return v;
}
void _proveForward;
void _proveBackward;

// --- Reguły custom --------------------------------------------------------

export const CUSTOM_RULE_ACTIONS = [
  'block',
  'managed_challenge',
  'js_challenge',
  'skip',
  'log',
] as const;

export const customRuleSchema = z.object({
  /** Stabilny identyfikator — składnik `ref` reguły w Cloudflare. */
  slug: z
    .string()
    .min(1)
    .max(48)
    .regex(/^[a-z0-9_]+$/, 'Dozwolone są tylko małe litery, cyfry i podkreślenia.'),
  /** Prefiks namespace dokładany automatycznie, żeby odróżnić nasze reguły od reguł klienta. */
  description: z
    .string()
    .min(1, 'Opis nie może być pusty.')
    .max(200)
    .transform((d) =>
      d.startsWith(RULE_DESCRIPTION_PREFIX) ? d : `${RULE_DESCRIPTION_PREFIX} ${d}`,
    ),
  expression: z.string().min(1, 'Wyrażenie nie może być puste.'),
  action: z.enum(CUSTOM_RULE_ACTIONS),
  /** Etykieta pokazywana klientowi. Prostym językiem, bez żargonu. */
  clientLabel: z.string().min(1, 'Etykieta dla klienta nie może być pusta.').max(120),
});

export type CustomRule = z.infer<typeof customRuleSchema>;

// --- Ustawienie tymczasowe ------------------------------------------------

/**
 * Automatyczne cofnięcie ustawienia.
 *
 * `revertTo` jest OPCJONALNYM nadpisaniem. Domyślnie cofamy do wartości
 * odczytanej ze strefy PRZED zmianą (snapshot), żeby klient wrócił dokładnie
 * do stanu wyjściowego, a nie do naszego domysłu.
 */
export const temporarySchema = z.object({
  setting: z.enum(CATALOG_KEYS as readonly [CatalogKey, ...CatalogKey[]]),
  afterMinutes: z
    .number()
    .int()
    .min(5, 'Minimum 5 minut.')
    .max(1440, 'Maksimum 24 godziny.')
    .default(120),
  revertTo: z.unknown().optional(),
});

export type TemporaryRevert = z.infer<typeof temporarySchema>;

// --- Preset ---------------------------------------------------------------

export const presetInputSchema = z
  .object({
    name: z.string().min(1, 'Nazwa presetu jest wymagana.').max(120),
    clientHeadline: z.string().min(1, 'Nagłówek dla klienta jest wymagany.').max(120),
    clientDescription: z
      .string()
      .min(1, 'Opis dla klienta jest wymagany.')
      .max(600, 'Opis powinien mieć 2–3 zdania.'),
    items: presetItemsSchema.default({}),
    customRules: z.array(customRuleSchema).max(20).default([]),
    temporary: temporarySchema.optional(),
  })
  .superRefine((preset, ctx) => {
    // 1. Wartości zablokowane w katalogu (np. ssl: flexible) nie mogą trafić do presetu.
    for (const [key, value] of Object.entries(preset.items)) {
      if (value === undefined) continue;
      if (!isBlockedValue(key as CatalogKey, value)) continue;
      ctx.addIssue({
        code: 'custom',
        path: ['items', key],
        message: valueNote(key as CatalogKey, value)?.textPl ?? 'Ta wartość jest zabroniona.',
      });
    }

    // 2. Under Attack Mode ZAWSZE musi być tymczasowy.
    if (preset.items.security_level === 'under_attack') {
      if (!preset.temporary || preset.temporary.setting !== 'security_level') {
        ctx.addIssue({
          code: 'custom',
          path: ['temporary'],
          message:
            'Tryb ataku (under_attack) musi mieć ustawione automatyczne cofnięcie dla ' +
            '„security_level" — inaczej zostanie włączony na stałe i zablokuje płatności ' +
            'oraz integracje klienta.',
        });
      }
    }

    // 3. Nadpisanie `revertTo` musi być poprawną wartością dla swojego ustawienia.
    const temporary = preset.temporary;
    if (temporary?.revertTo !== undefined) {
      if (!isAllowedCatalogValue(temporary.setting, temporary.revertTo)) {
        ctx.addIssue({
          code: 'custom',
          path: ['temporary', 'revertTo'],
          message: `Wartość spoza dozwolonych dla ustawienia „${temporary.setting}".`,
        });
      }
    }

    // 4. Cofać można tylko to, co preset faktycznie ustawia.
    if (temporary && preset.items[temporary.setting] === undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['temporary', 'setting'],
        message: `Preset nie ustawia „${temporary.setting}", więc nie ma czego cofać.`,
      });
    }

    // 5. Slugi reguł custom muszą być unikalne — z nich powstaje `ref`.
    const slugs = preset.customRules.map((r) => r.slug);
    const duplicates = slugs.filter((s, i) => slugs.indexOf(s) !== i);
    if (duplicates.length > 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['customRules'],
        message: `Powtórzone identyfikatory reguł: ${[...new Set(duplicates)].join(', ')}.`,
      });
    }
  });

export type PresetInput = z.infer<typeof presetInputSchema>;

export const presetSchema = z.object({
  id: z.string(),
  name: z.string(),
  clientHeadline: z.string(),
  clientDescription: z.string(),
  items: presetItemsSchema,
  customRules: z.array(customRuleSchema),
  temporary: temporarySchema.optional(),
  createdBy: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type Preset = z.infer<typeof presetSchema>;

/** Klucze faktycznie ustawiane przez preset — wejście do `requiredScopes`. */
export function presetKeys(items: PresetItems): readonly CatalogKey[] {
  return CATALOG_KEYS.filter((key) => items[key] !== undefined);
}
